import React, { useEffect, useState } from "react";
import { useQuery, useMutation, NetworkStatus } from "@apollo/client";
import CancelModal from "./components/cancel-modal";
import Header from "../../../../components/common/header";
import { useNavigate } from "react-router-dom";
import { GET_SELLER_STORE } from "../../api/seller-store";
import LoadingSpinnerInline from "../../../../components/common/loading-spinner/loading-spinner-inline";
import clsx from "clsx";
import LoadingSpinnerFull from "../../../../components/common/loading-spinner/loading-spinner-full";
import ResponsiveCDNImage from "../../../../components/common/image/responsive-cdn-image";
import { HiExclamationTriangle } from "react-icons/hi2";
import { notify, notifyError } from "../../../../utils/toast";
import {
  CREATE_REPORT,
} from "../../api/pack-order-queries";
import { TOGGLE_PICKUP_COMPLETED } from "../../../../api/order-mutations";
import IMG1 from "./img.png";
import { PiCallBellDuotone } from "react-icons/pi";
import { FaHistory } from "react-icons/fa";
import PickupNoticeModal from "../../components/pickup-notice-modal";
import { IoIosArrowUp, IoIosArrowDown } from "react-icons/io";
import SaleStatus from "./components/sale-status";
import { GET_PRODUCTS, DELETE_PRODUCT, CANCEL_PAYMENT } from "../../api/live-order-queries";
import { OrderItem, Product } from "../../../../__generated__/graphql";
import QuantityEditModal from "./components/quantity-edit-modal";
import { UPDATE_PRODUCT_WITH_MENU } from "./sales-product-update/page";
import StopSaleModal from "./components/stop-sale-modal";
import { BLOCK_USER } from "../../../../api/blacklist";
import { HiXMark } from "react-icons/hi2";

type Store = {
  id: number;
  img?: string;
  title: string;
  address: string;
  contactNumber: string;
  closingHours: string;
  lat: number;
  lng: number;
};

const LiveOrderSales: React.FC = () => {
  const navigate = useNavigate();
  const PAGE_SIZE = 30;
  const productsQueryVars = {
    take: PAGE_SIZE,
    cursor: null,
    dateScope: "TODAY",
  } as const;

  const [openStates, setOpenStates] = useState<{ [key: number]: boolean }>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<any>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [isStopSaleModalOpen, setIsStopSaleModalOpen] = useState(false);
  const [updatePack] = useMutation(UPDATE_PRODUCT_WITH_MENU);
  const [cancelingProduct, setCancelingProduct] = useState<any>();
  const [reason, setReason] = useState("");
  const [loadingProductId, setLoadingProductId] = useState<number | null>(null);
  const { loading, error, data, refetch, fetchMore, networkStatus } = useQuery(
    GET_PRODUCTS,
    {
      fetchPolicy: "network-only",
      variables: productsQueryVars,
      notifyOnNetworkStatusChange: true,
    }
  );

  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    if (loading) return;
    const firstCount = data?.productsBySeller?.length ?? 0;
    // fetchMore로 누적되면 length가 PAGE_SIZE를 초과하므로, "1페이지(초기/리페치)" 구간에서만 재계산
    if (firstCount <= PAGE_SIZE) {
      setHasMore(firstCount === PAGE_SIZE);
    }
  }, [loading, data, PAGE_SIZE]);

  const handleLoadMore = () => {
    if (loading || isFetchingMore || !hasMore) return;
    const current = data?.productsBySeller ?? [];
    if (current.length === 0) return;

    const lastId = current[current.length - 1]?.id;
    if (!lastId) return;

    let lastPageCount = 0;
    fetchMore({
      variables: {
        take: PAGE_SIZE,
        cursor: lastId,
        dateScope: "TODAY",
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        const prevList = prev?.productsBySeller ?? [];
        const nextList = fetchMoreResult?.productsBySeller ?? [];
        lastPageCount = nextList.length;

        const merged = new Map<number, any>();
        [...prevList, ...nextList].forEach((p: any) => merged.set(p.id, p));

        // cursor(lastId)가 제대로 전진하도록 정렬 고정 (createdAt desc, id desc)
        const sorted = Array.from(merged.values()).sort((a: any, b: any) => {
          const aTime = new Date(a.createdAt).getTime();
          const bTime = new Date(b.createdAt).getTime();
          if (aTime !== bTime) return bTime - aTime;
          return (b.id ?? 0) - (a.id ?? 0);
        });

        return {
          ...prev,
          productsBySeller: sorted,
        };
      },
    })
      .then((res) => {
        // res.data는 merge된 전체 리스트일 수 있어서, "이번 페이지" 길이로 판단
        if (lastPageCount < PAGE_SIZE) setHasMore(false);
      })
      .catch(() => {
        // 무한 스크롤 추가 로딩 실패는 조용히 무시
      });
  };

  const [deleteProduct] = useMutation(DELETE_PRODUCT, {
    refetchQueries: [{ query: GET_PRODUCTS, variables: productsQueryVars }],
  });
  const [cancelPayment] = useMutation(CANCEL_PAYMENT, {
    refetchQueries: [{ query: GET_PRODUCTS, variables: productsQueryVars }],
  });
  const [completePickup] = useMutation(TOGGLE_PICKUP_COMPLETED, {
    refetchQueries: [{ query: GET_PRODUCTS, variables: productsQueryVars }],
  });

  const [store, setStore] = useState<Store | null>(null);
  const [isOn, setIsOn] = useState(false);

  useQuery(GET_SELLER_STORE, {
    onCompleted: (data) => {
      setStore(data.getSellerStore);
      const hasProducts =
        data.getSellerStore?.todaysProducts &&
        data.getSellerStore?.todaysProducts.length !== 0;
      setIsOn(hasProducts);
    },
  });

  const [isReportModalOpen, setReportModalOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [selectedReportOrderId, setSelectedReportOrderId] = useState<
    number | null
  >(null);
  const [createReport, { loading: reportLoading }] = useMutation(CREATE_REPORT);
  const [showPickupNotice, setShowPickupNotice] = useState(() => {
    return !localStorage.getItem("boss_pickup_notice_seen");
  });

  // 유저 차단 관련 상태
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedUserName, setSelectedUserName] = useState<string>("");
  const [blockUser] = useMutation(BLOCK_USER, {
    refetchQueries: [{ query: GET_PRODUCTS, variables: productsQueryVars }],
  });

  const handleEdit = (productId: number) => {
    const target = data.productsBySeller.filter(
      (item: any) => item.id === productId
    );

    if (target[0].menus.length >= 1) {
      navigate(
        `/boss/live-order/sales/product-update/${productId}?quantity=${target[0].quantity}`
      ); // 메뉴에서 선택한 마감팩 업데이트
    } else {
      navigate(
        `/boss/live-order/sales/manual-update/${productId}?quantity=${target[0].quantity}`
      ); // 직접 만든 마감팩 업데이트
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("정말로 삭제하시겠습니까?")) {
      setLoadingProductId(id);
      await deleteProduct({
        variables: { id },
        onCompleted: () => {
          notify("삭제되었습니다.");
        },
        onError: (error) => {
          notifyError(error.message);
          refetch();
        },
      });
      setLoadingProductId(null);
    }
  };

  const handleCancel = (orderItem: any) => {
    setCancelingProduct(orderItem);
    setIsModalOpen(true);
  };

  const onConfirm = () => {
    cancelPayment({
      variables: {
        id: cancelingProduct.order.id,
        reason,
      },
      onCompleted: () => {
        notify("🔴 취소되었습니다.");
      },
      onError: (error) => {
        notifyError(error.message);
      },
    });
  };

  const handleEditQuantity = (product: Product) => {
    setEditingProduct(product);
    setSelectedProduct(product);
    setIsEditModalOpen(true);
  };

  const handleCount = (type: "increase" | "decrease") => {
    if (type === "increase") {
      setSelectedProduct({
        ...selectedProduct,
        quantity: (selectedProduct?.quantity || 0) + 1,
      });
    } else {
      if (selectedProduct?.quantity && selectedProduct?.quantity > 1) {
        setSelectedProduct({
          ...selectedProduct,
          quantity: (selectedProduct?.quantity || 0) - 1,
        });
      }
    }
  };

  const onConfirmEditQuantity = () => {
    if (editingProduct.currentPurchasedCount > selectedProduct.quantity) {
      notifyError("팩 개수는 판매된 횟수보다 작을 수 없습니다.");
      return;
    }
    updatePack({
      variables: {
        input: {
          id: selectedProduct?.id,
          name: selectedProduct?.name,
          price: selectedProduct?.price,
          quantity: selectedProduct?.quantity,
        },
      },
      onCompleted: () => {
        notify("팩 개수가 수정되었습니다.");
      },
      onError: (error) => {
        notifyError(error.message);
      },
    });
  };

  const handleStopSale = (product: Product) => {
    setEditingProduct(product);
    setSelectedProduct(product);
    setIsStopSaleModalOpen(true);
  };

  const onConfirmStopSale = () => {
    updatePack({
      variables: {
        input: {
          id: selectedProduct?.id,
          name: selectedProduct?.name,
          price: selectedProduct?.price,
          quantity: selectedProduct?.quantity,
          isEnd: true,
        },
      },
      onCompleted: () => {
        notify("판매종료되었습니다.");
      },
      onError: (error) => {
        notifyError(error.message);
      },
    });
  };

  const handleReason = (reason: string) => {
    setReason(reason);
  };

  const handleRefresh = () => {
    window.location.reload(); // 페이지 새로고침
  };

  const handleReportOrder = (orderId: number, reason: string) => {
    createReport({
      variables: { orderId, reason },
      onCompleted: () => {
        notify("신속하게 확인 후에 연락드리겠습니다.");
      },
      onError: (error) => {
        notify(error.message);
      },
    });
  };

  const handleBlockUser = (userId: number, userName?: string) => {
    setSelectedUserId(userId);
    setSelectedUserName(userName || "");
    setIsBlockModalOpen(true);
  };

  const selectedDisplayName = selectedUserName || "익명의 히어로";

  const confirmBlockUser = async () => {
    if (selectedUserId !== null) {
      try {
        const result = await blockUser({
          variables: { userId: selectedUserId },
        });

        if (result.data?.blockUser?.ok) {
          notify("유저가 차단되었습니다.");
          setIsBlockModalOpen(false);
          setSelectedUserId(null);
          setSelectedUserName("");
        } else {
          notifyError(
            result.data?.blockUser?.error || "유저 차단에 실패했습니다."
          );
        }
      } catch (err: any) {
        notifyError(err.message || "유저 차단에 실패했습니다.");
      }
    }
  };

  const handleCompletePickup = (orderId: number) => {
    completePickup({
      variables: { id: orderId, triggeredBy: "seller" },
    });
  };

  // fetchMore 중에도 loading=true가 될 수 있어서, "초기 로딩"일 때만 풀 스피너 노출
  if (loading && !data) return <LoadingSpinnerFull />;
  if (error && !data) return <p>오류: {error.message}</p>;

  if (!store)
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
        <p className="text-lg mb-6 text-gray-700">
          매장이 아직 등록되지 않았습니다.
        </p>
        <button
          onClick={() => navigate("/boss/my/store-management")}
          className="px-6 py-3 bg-blue-500 text-white font-semibold rounded-lg shadow-md hover:bg-blue-600 transition duration-300"
        >
          매장 추가하러 가기
        </button>
      </div>
    );

  const isToday = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const todayOrders = (data?.productsBySeller ?? []).filter((product: any) =>
    isToday(product.createdAt)
  );

  const toggleDetails = (id: number) => {
    setOpenStates((prev) => ({
      ...prev,
      [id]: !prev[id], // 해당 카드의 상태를 토글
    }));
  };

  return (
    <div className="min-h-screen bg-white pb-20">
      <Header
        title="오늘 주문 현황"
        showBackButton
        onBackClick={() => navigate("/boss/home", { replace: true })}
      />

      <div className="">
        <div className="flex justify-between pt-4 px-4 border-b-2 border-gray-200 pb-4">
          <div className="flex flex-col items-start">
            <h2 className="text-4xl font-bold text-center text-gray-800">
              {`${
                new Date().getMonth() + 1
              }/${new Date().getDate()} ${new Date().toLocaleDateString(
                "ko-KR",
                {
                  weekday: "long",
                }
              )}`}
            </h2>
            <p className="text-gray-500 text-lg break-keep">
              오늘 실시간 판매 내역이에요.
            </p>
            {/* 실시간 판매 진행 여부 표시 */}
            <p className="text-lg font-semibold flex items-center gap-2 mt-2">
              <div
                className={clsx(
                  "w-5 h-5 rounded-full transition-colors shadow-sm",
                  isOn
                    ? "bg-blue-500 ring-2 ring-blue-200"
                    : "bg-gray-500 ring-2 ring-gray-200"
                )}
              ></div>
              <span
                className={clsx(
                  "text-lg font-semibold",
                  isOn ? "text-blue-500" : "text-gray-500"
                )}
              >
                {isOn ? "실시간 판매 진행중" : "실시간 판매 미노출"}
              </span>
            </p>
          </div>
          <div className="flex flex-col items-center justify-center">
            <button
              onClick={handleRefresh}
              className="flex text-lg font-bold items-center justify-center px-6 p-4 w-full bg-blue-500 text-white rounded-xl hover:bg-blue-600"
            >
              새로고침
            </button>
          </div>
        </div>
        {todayOrders.length > 0 ? (
          <div className="px-4 overflow-scroll">
            {/* 해당 날짜에 속하는 제품들 렌더링 */}
            {/* TODO: any 쓰지말자 */}

            {todayOrders
              ?.filter(
                (product: any) =>
                  product.isDeleted === false || product.isDeleted === null
              )
              .map((product: any) => {
                const isOpen = openStates[product.id] || false; // 상태 가져오기

                return (
                  <div
                    key={product.id}
                    className={`flex overflow-scroll flex-col mt-6 ${
                      product.order?.isPickupCompleted
                        ? "bg-green-100"
                        : "bg-white"
                    }`}
                  >
                    {/* report button */}
                    {/* 판매가 이루어진 팩만 문의가 가능 */}
                    {product.order?.id && (
                      <div className="flex justify-between items-center">
                        <div className="text-2xl font-bold text-gray-600">
                          {product.order?.user?.name || "익명의 히어로"}
                        </div>
                        <div className="flex gap-2">
                          {product.order?.user?.id && (
                            <button
                              onClick={() => {
                                if (
                                  product.order?.user?.id &&
                                  product.order?.user?.name
                                ) {
                                  handleBlockUser(
                                    product.order.user.id,
                                    product.order.user.name
                                  );
                                }
                              }}
                              className="flex items-center gap-2 bg-white border-red-500 border text-red-500 px-3 py-1 rounded"
                            >
                              차단
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setReportModalOpen(true);
                              setSelectedReportOrderId(product.order?.id);
                              setReportReason("");
                            }}
                            className="flex items-center gap-2 bg-white border-red-500 border text-red-500 px-3 py-1 rounded"
                          >
                            <HiExclamationTriangle className="text-red-500" />{" "}
                            문의
                          </button>
                        </div>
                      </div>
                    )}

                    {/* image list */}
                    <div className="flex overflow-x-scroll">
                      {product.menus?.length > 0 ? (
                        <div className="flex gap-1">
                          {[...product?.menus]
                            .reverse()
                            .map((menu: any, index: number) => {
                              return (
                                <div
                                  key={menu.id}
                                  className="relative w-32 h-32"
                                >
                                  <ResponsiveCDNImage
                                    src={menu.img}
                                    alt=""
                                    className="w-32 h-32 rounded-md object-cover"
                                    sizeRatio={1 / 2}
                                  />
                                  <div className="absolute bottom-1 right-1.5 rounded-md opacity-80 bg-black text-white p-0.5 px-1.5">
                                    <p className="text-md font-semibold">
                                      x{menu.quantity}
                                    </p>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      ) : (
                        <div className="w-[137px] h-[137px]">
                          <ResponsiveCDNImage
                            src={product?.img}
                            alt="Menu"
                            className="w-[137px] h-[137px] object-cover rounded-lg"
                            sizeRatio={1 / 2}
                          />
                        </div>
                      )}
                    </div>

                    <div className="flex w-full flex-col">
                      <div className="flex">
                        <div className="text-xl break-keep font-semibold mt-2 max-w-48">
                          {product.name}
                        </div>
                      </div>
                      <div className="mt-1">
                        <div className="flex justify-between items-center">
                          <div className="flex">
                            <p className="text-gray-400 text-base">
                              정가: {product.price.toLocaleString()}
                            </p>
                            <p className="text-gray-400 text-base ml-1">
                              할인가: {product.discountPrice?.toLocaleString()}
                            </p>
                          </div>
                          <div className="flex justify-end">
                            <p className="text-xl text-gray-900 font-bold">
                              {product?.userPrice
                                ? Math.floor(product.userPrice).toLocaleString()
                                : Math.floor(
                                    ((product.discountPrice +
                                      product.discountPrice * 0.1) /
                                      10) *
                                      10
                                  ).toLocaleString()}
                              원<span className="text-gray-900"></span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* 안 팔렸으면 수정/삭제 */}
                    {/*   팔렸으면 개수수정 */}

                    <div className="flex justify-end mt-4 font-semibold">
                      {isOn && !product.isEnd && (
                        <div className="">
                          <div
                            className="px-2 py-2 bg-blue-500 text-white font-bold rounded text-lg"
                            onClick={() => handleEditQuantity(product)}
                          >
                            개수 수정
                          </div>
                        </div>
                      )}
                      {isOn &&
                        product.orderItems.length > 0 &&
                        !product.isEnd &&
                        product.quantity !== product.currentPurchasedCount && (
                          <div className="ml-2">
                            <div
                              className="px-2 py-2 bg-red-500 text-white font-bold rounded text-lg"
                              onClick={() => handleStopSale(product)}
                            >
                              판매종료
                            </div>
                          </div>
                        )}
                      {product.orderItems.length < 1 && isOn && (
                        <div className="flex ml-2 gap-x-2">
                          <button
                            className="px-2 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                            onClick={() => handleEdit(product.id)}
                          >
                            메뉴/할인율 수정
                          </button>
                          <button
                            className={clsx(
                              "px-2 py-2 bg-red-500 text-white rounded hover:bg-red-600 min-w-12 h-10",
                              loadingProductId === product.id &&
                                "pointer-events-none opacity-80"
                            )}
                            onClick={() => handleDelete(product.id)}
                            disabled={loadingProductId === product.id}
                          >
                            {loadingProductId === product.id ? (
                              <LoadingSpinnerInline />
                            ) : (
                              "삭제"
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                    {/* 오더 정보 */}
                    <div
                      className="mt-2 w-full bg-gray-200 pb-2 rounded-lg p-2"
                      onClick={() => toggleDetails(product.id)}
                    >
                      <div className="flex justify-between">
                        <SaleStatus
                          isOn={isOn}
                          isEnd={product?.isEnd}
                          quantity={product.quantity}
                          currentPurchasedCount={product.currentPurchasedCount}
                        />
                      </div>

                      {isOpen && (
                        <div className="flex justify-center mt-2 w-full">
                          <div className="text-xl font-bold">구매자 리스트</div>
                        </div>
                      )}
                      {isOpen && product.orderItems.length === 0 && (
                        <div className="flex justify-center my-4 w-full">
                          <div className="text-lg font-bold text-gray-500">
                            아직 구매자가 없어요
                          </div>
                        </div>
                      )}
                      {/* 프로덕트에 대한 주문정보 리스트 */}
                      {isOpen && (
                        <div className="flex flex-col justify-between mt-2 w-full gap-y-2">
                          {product.orderItems.map((orderItem: OrderItem) => {
                            return (
                              <div
                                key={orderItem.id}
                                className={`flex flex-col w-full ${
                                  orderItem.order?.isPickupCompleted
                                    ? "bg-green-100"
                                    : "bg-white"
                                } rounded-lg p-2 text-xl gap-y-2 pt-4`}
                              >
                                <div>
                                  <div className="flex justify-between border-b border-gray-200 pb-2">
                                    <span className="w-1/2 ml-1">주문코드</span>
                                    <span className="font-bold w-1/2 text-[#1562fc]">
                                      {orderItem.order?.identificationCode}
                                    </span>
                                  </div>
                                  <div className="flex justify-between border-b border-gray-200 pb-2">
                                    <span className="w-1/2 ml-1">구매자</span>
                                    <span className="font-bold w-1/2">
                                      {orderItem.order?.user?.name ||
                                        "이름없음"}
                                    </span>
                                  </div>
                                  <div className="flex justify-between border-b border-gray-200 pb-2">
                                    <span className="w-1/2 ml-1">번호</span>
                                    <span className="font-bold w-1/2">
                                      {orderItem.order?.user?.phone}
                                    </span>
                                  </div>
                                  <div className="flex justify-between border-b border-gray-200 pb-2">
                                    <span className="w-1/2 ml-1">수량</span>
                                    <span className="font-bold w-1/2">
                                      {orderItem.quantity} 개
                                    </span>
                                  </div>
                                  <div className="flex justify-between border-b border-gray-200 pb-2">
                                    <span className="w-1/2 ml-1">픽업시간</span>
                                    <span className="font-bold w-1/2">
                                      {orderItem.order?.pickUpTime}
                                    </span>
                                  </div>
                                  {orderItem.order &&
                                    !orderItem.order?.isCanceled &&
                                    orderItem.order?.isApproved && (
                                      <div className="flex px-2 py-2 bg-gray-100 border">
                                        <div className="flex items-center gap-2">
                                          손님이 픽업을 완료했나요?
                                          <button
                                            className={`flex items-center justify-center ml-2 w-12 h-6 rounded-full transition-colors duration-300 ${
                                              orderItem.order?.isPickupCompleted
                                                ? "bg-green-500"
                                                : "bg-gray-300"
                                            }`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleCompletePickup(
                                                orderItem.order.id
                                              );
                                            }}
                                          >
                                            <span
                                              className={`w-6 h-6 bg-white rounded-full transform transition-transform duration-300 ${
                                                orderItem.order
                                                  ?.isPickupCompleted
                                                  ? "translate-x-[12px]"
                                                  : "translate-x-[-12px]"
                                              }`}
                                            ></span>
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  <div className="flex justify-between items-center mt-2 font-semibold">
                                    {orderItem.order?.user?.id && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (
                                            orderItem.order?.user?.id &&
                                            orderItem.order?.user?.name
                                          ) {
                                            handleBlockUser(
                                              orderItem.order.user.id,
                                              orderItem.order.user.name
                                            );
                                          }
                                        }}
                                        className="flex items-center gap-2 bg-white border-red-500 border text-red-500 px-3 py-1 rounded text-sm"
                                      >
                                        차단
                                      </button>
                                    )}
                                    {!orderItem.order?.user?.id && <div></div>}
                                    <div className="flex items-center">
                                      {!orderItem.order?.isCanceled &&
                                        orderItem.order &&
                                        orderItem.order?.isApproved && (
                                          <button
                                            className="px-2 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleCancel(orderItem);
                                            }}
                                          >
                                            결제 취소
                                          </button>
                                        )}
                                      {orderItem.order?.isCanceled && (
                                        <div className="px-2 py-2 text-red-500 font-bold rounded text-lg">
                                          취소 완료
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* 펼쳐보기 토글 버튼 */}
                      {isOpen ? (
                        <div className="flex justify-center text-xl">
                          <span className="underline">접기</span>{" "}
                          <IoIosArrowUp className="text-2xl" />
                        </div>
                      ) : (
                        <div className="flex items-center justify-center">
                          <span className="underline">펼쳐보기</span>{" "}
                          <IoIosArrowDown className="text-2xl" />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-[300px] mt-20">
            <div className="flex justify-center items-center h-48">
              <img
                src={IMG1}
                alt="실시간판매"
                className="max-h-56 bg-gray-200 rounded-2xl"
              />
            </div>
            <p className="text-gray-500 text-xl font-bold text-center mt-8">
              실시간 판매를 시작해 봐요 🚀
            </p>
            <div className="flex gap-4 mt-8 w-full px-4 max-w-md">
              <button
                className="w-full px-4 py-2 bg-white shadow-md font-bold border-2 text-black rounded-xl break-keep"
                onClick={() => navigate("/boss/live-order/previous-sales")}
              >
                <FaHistory className="w-4 h-4 text-blue-400 inline-block mr-1 mb-0.5" />
                이전 주문 보기
              </button>
              <button
                className="w-full px-4 py-2 bg-white shadow-md font-bold border text-black rounded-xl"
                onClick={() => navigate("/boss/live-order/select-from-menu")}
              >
                <PiCallBellDuotone className="w-5 h-5 text-blue-400 inline-block mr-1" />
                판매시작
              </button>
            </div>
          </div>
        )}

        {/* 더 불러오기 */}
        {hasMore && todayOrders.length > 0 && (
          <div className="flex justify-center py-6 px-4">
            <button
              type="button"
              onClick={handleLoadMore}
              disabled={loading || isFetchingMore}
              className={clsx(
                "w-full max-w-md py-4 rounded-xl font-bold text-center transition-colors flex items-center justify-center",
                "bg-blue-500 text-white hover:bg-blue-600",
                (loading || isFetchingMore) && "opacity-70 pointer-events-none"
              )}
            >
              {isFetchingMore ? <LoadingSpinnerInline /> : "더 불러오기"}
            </button>
          </div>
        )}

        {isModalOpen && (
          <CancelModal
            title="취소환불 처리"
            message="정말로 취소하시겠어요? 사유를 적어주세요!"
            onConfirm={() => {
              onConfirm(); // 모달 닫기
              setIsModalOpen(false);
            }}
            onCancel={() => setIsModalOpen(false)} // 모달 취소
            reason={reason}
            handleReason={handleReason}
          />
        )}

        {isEditModalOpen && (
          <QuantityEditModal
            onConfirm={() => {
              onConfirmEditQuantity(); // 모달 닫기
              setIsEditModalOpen(false);
            }}
            onClose={() => setIsEditModalOpen(false)} // 모달 취소
            count={selectedProduct?.quantity || 0}
            handleCount={handleCount}
          />
        )}

        {isStopSaleModalOpen && (
          <StopSaleModal
            onClose={() => setIsStopSaleModalOpen(false)}
            isModalOpen={isStopSaleModalOpen}
            onConfirm={() => {
              onConfirmStopSale(); // 모달 닫기
              setIsStopSaleModalOpen(false);
            }}
          />
        )}

        {isReportModalOpen && selectedReportOrderId && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white p-8 min-w-[340px] max-w-[90vw] rounded shadow-lg">
              <div className="text-2xl font-bold mb-6 text-center">
                이 손님이 맞나요?
              </div>
              <div className="bg-gray-100 rounded-lg p-4 mb-4">
                <div className="text-xl text-gray-600 mb-1">
                  이름:{" "}
                  <span className="font-bold text-blue-700">
                    {data?.productsBySeller.find(
                      (prod: any) => prod.order?.id === selectedReportOrderId
                    )?.order?.user?.name || "익명의 히어로"}
                  </span>
                </div>
                <div className="text-xl text-gray-600">
                  전화번호:{" "}
                  <span className="font-bold text-blue-700">
                    {data?.productsBySeller.find(
                      (prod: any) => prod.order?.id === selectedReportOrderId
                    )?.order?.user?.phone || "없음"}
                  </span>
                </div>
              </div>
              <div className="text-xl text-gray-800 font-semibold mb-6">
                어떤 상황인지 알려주세요.
                <br />
                빠르게 검토하고 연락드릴게요!
              </div>
              <div className="text-gray-600 mb-4">
                <span className="font-bold">예시:</span>
                <ul>
                  <li>· 손님이 오지 않았나요?</li>
                  <li>· 손님이 무리한 요구를 했나요?</li>
                  <li>· 손님이 픽업시간보다 늦게 왔나요?</li>
                </ul>
              </div>
              <textarea
                className="w-full p-3 border border-gray-300 rounded mb-4 text-lg"
                value={reportReason}
                rows={5}
                onChange={(e: any) => setReportReason(e.target.value)}
                placeholder="상황을 입력해 주세요"
              />
              <div className="flex justify-center gap-4 mt-4">
                <button
                  className="bg-gray-500 text-white px-5 py-2 rounded"
                  onClick={() => {
                    setReportModalOpen(false);
                    setReportReason("");
                  }}
                >
                  취소
                </button>
                <button
                  className="bg-blue-500 text-white px-5 py-2 rounded"
                  onClick={() => {
                    if (window.confirm("문의를 제출할까요?")) {
                      handleReportOrder(selectedReportOrderId, reportReason);
                      setReportModalOpen(false);
                      setReportReason("");
                    }
                  }}
                  disabled={reportLoading || !reportReason.trim()}
                >
                  {reportLoading ? <LoadingSpinnerInline /> : "완료"}
                </button>
              </div>
            </div>
          </div>
        )}

        <PickupNoticeModal
          isOpen={showPickupNotice}
          onClose={() => setShowPickupNotice(false)}
          localStorageKey="boss_pickup_notice_seen"
        />

        {/* 유저 차단 확인 모달 */}
        {isBlockModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-800">
                  유저를 차단하시겠습니까?
                </h2>
                <button
                  onClick={() => {
                    setIsBlockModalOpen(false);
                    setSelectedUserId(null);
                    setSelectedUserName("");
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <HiXMark className="w-6 h-6" />
                </button>
              </div>

              <p className="text-lg text-gray-600 mb-2">
                <span className="font-semibold">{selectedDisplayName}</span>님을
                차단하시겠습니까?
              </p>
              <p className="text-gray-500 text-sm mb-6">
                차단된 유저는 매장이 노출되지 않으며 주문을 할 수 없습니다.
              </p>

              <div className="flex justify-end gap-3">
                <button
                  className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                  onClick={() => {
                    setIsBlockModalOpen(false);
                    setSelectedUserId(null);
                    setSelectedUserName("");
                  }}
                >
                  취소
                </button>
                <button
                  className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors"
                  onClick={confirmBlockUser}
                >
                  차단
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveOrderSales;
